/* eslint-disable quote-props */

/* eslint-disable no-constant-condition */
import { Injectable } from '@angular/core'

import { Classifier } from '@flowx/designer/modules/new-ai/modules/documents/components/ai-classifiers/classifiers.store'

import { environment } from '../../../../../../environments/environment'

const APPLICATION_UUID = 'f1b3b3b0-0b3b-4b3b-8b3b-0b3b3b3b3b3b'

@Injectable({ providedIn: 'root' })
export class DocumentsService {
  baseUrl = environment.production ? environment.baseUrl : ''
  async getAllDocuments(query: string, page: number = 1, size: number = 20): Promise<any> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/documents/getFiltered?page=${page}&size=${size}`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }
    const body = {
      query,
    }
    const options = {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  async getTopics(): Promise<any> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/classifiers/topics`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }

    const options = {
      method: 'GET',
      headers,
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  async getPrivateDocumentContent(application_id: string, url: string): Promise<any> {
    const token = localStorage.getItem('access_token')

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Fx-AppId': application_id,
    }

    const options = {
      method: 'GET',
      headers,
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  async getDocumentSettings(): Promise<any> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/settings`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }

    const options = {
      method: 'GET',
      headers,
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  async getDocumentById(id: string): Promise<any> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/documents/${id}`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }

    const options = {
      method: 'GET',
      headers,
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  async uploadDocument(files: any): Promise<any> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/files/upload`

    const formData = new FormData()
    for (const file of files) {
      formData.append('docs', file)
    }
    formData.append('application_id', APPLICATION_UUID)
    formData.append('wait_for_summary', 'true')
    const options = {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      if (error.name === 'AbortError') {
        return { stopped: true }
      } else {
        this.handleError(error)
      }
      return null
    }
  }

  async deleteDocument(id: string): Promise<any> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/documents/${id}`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }
    const options = {
      method: 'DELETE',
      headers,
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      if (error.name === 'AbortError') {
        return { stopped: true }
      } else {
        this.handleError(error)
      }
      return Promise.resolve([])
    }
  }

  // classifiers

  async getAllClassifiers(
    appId: string,
    query: string,
    page: number = 1,
    size: number = 20
  ): Promise<any> {
    const token = localStorage.getItem('access_token')

    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/classifiers/getFiltered/${appId}?page=${page}&size=${size}`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }
    const body = {
      query,
    }
    const options = {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  async getClassifierById(id: string): Promise<any> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/classifiers/${id}`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }

    const options = {
      method: 'GET',
      headers,
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  async duplicateClassifier(newClassifier: {
    name: string
    description: string
    code: string
    application_id: string
  }): Promise<Classifier> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/classifiers`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }
    const body = {
      ...newClassifier,
    }

    const options = {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  async createClassifier(newClassifier: {
    name: string
    description: string
    code: string
    application_id: string
  }): Promise<Classifier> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/classifiers`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }
    const body = {
      ...newClassifier,
    }

    const options = {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  async updateClassifier(newClassifier: {
    id: string
    name: string
    description: string
    code: string
    application_id: string
  }): Promise<Classifier> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/classifiers/${newClassifier.id}`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }
    const body = {
      ...newClassifier,
    }

    const options = {
      method: 'PUT',
      headers,
      body: JSON.stringify(body),
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  async deleteClassifier(id: string): Promise<null> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/classifiers/${id}`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }

    const options = {
      method: 'DELETE',
      headers,
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  // lenses

  async getAllLenses(
    classifierId: string,
    query: string,
    page: number = 1,
    size: number = 20
  ): Promise<any> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/lenses/getFiltered/${classifierId}?page=${page}&size=${size}`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }
    const body = {
      query,
    }
    const options = {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  async getLenseById(id: string): Promise<any> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/lenses/${id}`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }

    const options = {
      method: 'GET',
      headers,
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  async getImageContent(
    object_name: string,
    resource_id: string,
    content_type: string,
    application_id?: string,
    application_version_id?: string
  ): Promise<any> {
    const token = localStorage.getItem('access_token')
    let url = `${this.baseUrl}/di-platform/api/v1/di-platform/files/image?object_name=${encodeURIComponent(
      object_name
    )}&resource_id=${encodeURIComponent(resource_id)}&content_type=${encodeURIComponent(content_type)}`
    if (application_id) {
      url += `&application_id=${encodeURIComponent(application_id)}`
    }
    if (application_version_id) {
      url += `&application_version_id=${encodeURIComponent(application_version_id)}`
    }

    const headers = {
      Authorization: `Bearer ${token}`,
    }

    const options = {
      method: 'GET',
      headers,
    }

    // convert ArrayBuffer to base64
    const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
      const bytes = new Uint8Array(buffer)
      const chunkSize = 0x8000
      let binary = ''
      for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)))
      }
      return btoa(binary)
    }

    try {
      const response: Response = await fetch(url, options)
      if (!response.ok) {
        const text = await response.text().catch(() => '')
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText} ${text}`)
      }

      const contentType =
        response.headers.get('content-type') || content_type || 'application/octet-stream'
      const buffer = await response.arrayBuffer()
      const base64 = arrayBufferToBase64(buffer)

      return {
        content: base64,
        content_type: contentType,
      }
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  async createLense(newLense: {
    classifier_id: string
    name: string
    description: string
    code: string
  }): Promise<any> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/lenses`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }
    const body = {
      ...newLense,
    }

    const options = {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  async duplicateLense(newLense: {
    classifier_id: string
    name: string
    description: string
    code: string
  }): Promise<any> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/lenses`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }
    const body = {
      ...newLense,
    }

    const options = {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  async updateLense(newLense: {
    id: string
    classifier_id: string
    name: string
    description: string
    code: string
  }): Promise<any> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/lenses/${newLense.id}`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }
    const body = {
      ...newLense,
    }

    const options = {
      method: 'PUT',
      headers,
      body: JSON.stringify(body),
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  async deleteLense(id: string): Promise<null> {
    const token = localStorage.getItem('access_token')
    const url = `${this.baseUrl}/di-platform/api/v1/di-platform/lenses/${id}`

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }

    const options = {
      method: 'DELETE',
      headers,
    }
    try {
      const response: Response = await fetch(url, options)
      return response.json()
    } catch (error) {
      this.handleError(error)
      return null
    }
  }

  /**
   * Handles any errors that occur during the chat completion request process.
   *
   * u/param {any} error - The error object to handle.
   */
  private handleError(error: any): void {
    console.error(error)
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('Request was aborted')
    } else if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('Network error occurred')
    } else if (error instanceof TypeError && error.message === 'Failed to decode') {
      throw new Error('Error decoding response from server')
    } else if (
      error instanceof TypeError &&
      error.message === 'JSON.parse: unexpected end of data at line 1 column 1 of the JSON data'
    ) {
      throw new Error('Invalid JSON response from server')
    } else if (
      error instanceof TypeError &&
      error.message === 'response.body?.pipeThrough(...).getReader is not a function'
    ) {
      throw new Error('Invalid response from server')
    } else {
      throw new Error('Unknown error occurred')
    }
  }
}
